import { apiClient } from '../../shared/api/axios-instance'
import type { AdminUser, AuthResponse, LoginCredentials, MFAVerification, MfaSetupResponse, AdminSession } from '../types'

class AuthService {
  /**
   * Admin login with email and password
   */
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    const response = await apiClient.post('/admin/auth/login', credentials)
    return response.data
  }

  /**
   * Verify MFA code (Login Step 2)
   */
  async verifyMFA(verification: MFAVerification & { temp_token: string }): Promise<AuthResponse> {
    const response = await apiClient.post('/admin/auth/mfa/verify', verification)
    return response.data
  }

  /**
   * Setup MFA (Generate QR)
   */
  async setupMFA(tempToken: string): Promise<MfaSetupResponse> {
    const response = await apiClient.post('/admin/auth/mfa/setup', { temp_token: tempToken })
    return response.data
  }

  /**
   * Verify Setup (Enrollment Step 2)
   */
  async verifySetup(data: { temp_token: string, code: string }): Promise<AuthResponse> {
    const response = await apiClient.post('/admin/auth/mfa/verify-setup', data)
    return response.data
  }

  /**
   * Refresh access token
   */
  async refreshToken(refreshToken: string): Promise<AuthResponse> {
    const response = await apiClient.post('/admin/auth/refresh', {
      refresh_token: refreshToken
    })
    return response.data
  }

  /**
   * Logout current admin
   */
  async logout(): Promise<void> {
    const refreshToken = this.getRefreshToken()
    await apiClient.post('/admin/auth/logout', { refresh_token: refreshToken })
    this.clearTokens()
  }

  /**
   * Get current admin profile
   */
  async getCurrentAdmin(): Promise<AdminUser> {
    const response = await apiClient.get('/admin/auth/me')
    const d = response.data?.data || response.data
    return {
      id: d.id,
      email: d.email,
      name: d.name,
      phone: d.phone,
      avatarUrl: d.avatar_url,
      role: d.role,
      permissions: d.permissions,
      mfaEnabled: d.mfa_enabled,
      lastLoginAt: d.last_login_at,
      lastLoginIp: d.last_login_ip,
    }
  }

  /**
   * Get active admin sessions
   */
  async getActiveSessions(): Promise<AdminSession[]> {
    const response = await apiClient.get('/admin/sessions') // Check route?
    return response.data
  }

  /**
   * Force logout a specific session
   */
  async forceLogoutSession(sessionId: string): Promise<void> {
    await apiClient.delete(`/admin/sessions/${sessionId}`)
  }

  /**
   * Store auth tokens in localStorage
   */
  storeTokens(accessToken: string, refreshToken: string): void {
    localStorage.setItem('admin_access_token', accessToken)
    localStorage.setItem('admin_refresh_token', refreshToken)
  }

  /**
   * Clear auth tokens from localStorage
   */
  clearTokens(): void {
    localStorage.removeItem('admin_access_token')
    localStorage.removeItem('admin_refresh_token')
  }

  /**
   * Get stored access token
   */
  getAccessToken(): string | null {
    return localStorage.getItem('admin_access_token')
  }

  /**
   * Get stored refresh token
   */
  getRefreshToken(): string | null {
    return localStorage.getItem('admin_refresh_token')
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return !!this.getAccessToken()
  }
}

export const authService = new AuthService()
